import { z } from 'zod';
import { tagNameSchema } from '../tag/tag';
import {
  ageGroupSchema,
  ageGroupsSchema,
  difficultySchema,
  explanationSchema,
  Language,
  languageSchema,
  numericAnswerSchema,
  rejectionReasonSchema,
  sourceReferenceSchema,
  targetLanguagesSchema,
  Task,
  taskIdSchema,
  taskSchema,
  taskStatusSchema,
  TaskTranslation,
  taskTranslationSchema,
  TaskType,
  taskTypeSchema,
  taskTypesSchema,
  tagsSchema,
  textSchema,
  timestampSchema,
} from './task';

/** The task bank's collection path (QZ-D42). */
export const TASKS_PATH = '/tasks';

/** A single task's path (QZ-D42). */
export const taskPath = (taskId: string): string => `/tasks/${encodeURIComponent(taskId)}`;

/** A single task's status path (QZ-D37). */
export const taskStatusPath = (taskId: string): string => `${taskPath(taskId)}/status`;

/** The router pattern matching `taskPath` (QZ-D42). */
export const TASK_PATH_PATTERN = '/tasks/:taskId';

/** The router pattern matching `taskStatusPath` (QZ-D37). */
export const TASK_STATUS_PATH_PATTERN = '/tasks/:taskId/status';

/** The number of tasks returned by one page of `GET /tasks` (QZ-D38). */
export const TASK_PAGE_SIZE = 25;

/**
 * The query string accepted by `GET /tasks` (QZ-D42, QZ-D105). `document` and `section` filter
 * approved tasks only; that rule lives in `TaskService`, not here, so this schema carries **no
 * refinement**: the web's `taskListFilters.ts` builds its filter schema with
 * `listTasksQuerySchema.omit({ cursor: true })`, and zod 4's `.omit` throws on an object schema
 * carrying a `superRefine`.
 */
export const listTasksQuerySchema = z
  .object({
    status: taskStatusSchema.default('draft'),
    tag: tagNameSchema.optional(),
    taskType: taskTypeSchema.optional(),
    difficulty: difficultySchema.optional(),
    ageGroup: ageGroupSchema.optional(),
    language: languageSchema.optional(),
    jobId: taskIdSchema.optional(),
    document: textSchema.max(200).optional(),
    section: textSchema.max(200).optional(),
    cursor: z.string().min(1).optional(),
  })
  .strict();
export type ListTasksQuery = z.infer<typeof listTasksQuerySchema>;

/** How many tasks match the task list's filters (QZ-D160). */
export const TASKS_COUNT_PATH = '/tasks/count';

/** The query string accepted by `GET /tasks/count`: the list's filters, with no cursor (QZ-D160). */
export const countTasksQuerySchema = listTasksQuerySchema.omit({ cursor: true });
export type CountTasksQuery = z.infer<typeof countTasksQuerySchema>;

/** The body returned by `GET /tasks/count` (QZ-D160). */
export const countTasksResponseSchema = z.object({ count: z.number().int().nonnegative() }).strip();
export type CountTasksResponse = z.infer<typeof countTasksResponseSchema>;

/** The body returned by `GET /tasks` (QZ-D42). */
export const listTasksResponseSchema = z
  .object({
    tasks: z.array(taskSchema),
    nextCursor: z.string().min(1).optional(),
  })
  .strip();
export type ListTasksResponse = z.infer<typeof listTasksResponseSchema>;

/** The body accepted by `PUT /tasks/:taskId` to replace a draft's editable content (QZ-D41). */
export const updateTaskRequestSchema = z
  .object({
    expectedUpdatedAt: timestampSchema,
    taskTypes: taskTypesSchema,
    difficulty: difficultySchema,
    tags: tagsSchema,
    targetLanguages: targetLanguagesSchema,
    ageGroups: ageGroupsSchema,
    numericAnswer: numericAnswerSchema.optional(),
    sourceReference: sourceReferenceSchema.optional(),
    explanation: explanationSchema,
    original: taskTranslationSchema,
  })
  .strict();
export type UpdateTaskRequest = z.infer<typeof updateTaskRequestSchema>;

/** The body accepted by `POST /tasks/:taskId/status` (QZ-D37). */
export const changeTaskStatusRequestSchema = z
  .object({
    status: taskStatusSchema,
    rejectionReason: rejectionReasonSchema.optional(),
    expectedUpdatedAt: timestampSchema,
  })
  .strict()
  .superRefine((value, ctx) => {
    if (value.rejectionReason !== undefined && value.status !== 'rejected') {
      ctx.addIssue({
        code: 'custom',
        path: ['rejectionReason'],
        message: 'A rejection reason is only allowed when rejecting.',
      });
    }
  });
export type ChangeTaskStatusRequest = z.infer<typeof changeTaskStatusRequestSchema>;

/**
 * Whether `a` (a stored translation, possibly absent) still matches `b` (a parsed translation):
 * `text` and `freetextAnswer` equal, and `choices` and `items` each either both absent or equal
 * element by element in order. A reordered choice list counts as changed wording, since the
 * first choice is the correct one (QZ-D16).
 */
const sameTranslation = (a: TaskTranslation | undefined, b: TaskTranslation): boolean => {
  if (a === undefined) {
    return false;
  }
  const sameList = (x: readonly string[] | undefined, y: readonly string[] | undefined): boolean =>
    x === undefined || y === undefined
      ? x === y
      : x.length === y.length && x.every((value, index) => value === y[index]);
  return (
    a.text === b.text &&
    a.freetextAnswer === b.freetextAnswer &&
    sameList(a.choices, b.choices) &&
    sameList(a.items, b.items)
  );
};

/** Whether `a` and `b` list the same task types, in any order (sets of equal size). */
const sameTaskTypes = (a: readonly TaskType[], b: readonly TaskType[]): boolean => {
  const setA = new Set(a);
  const setB = new Set(b);
  return setA.size === setB.size && [...setA].every((type) => setB.has(type));
};

/**
 * The stored task with a review edit applied (QZ-D41): the request's editable fields replace
 * the task's, `original` replaces `translations[task.originalLanguage]`, optional fields the
 * request omits are removed, and `updatedAt` is set. The result is unvalidated (QZ-D42).
 *
 * The edit drops every non-original translation when it changes the original translation (its
 * text, choices, free-text answer or items, choice/item order included) or the task types;
 * otherwise it drops only those the new target languages no longer include. The `translation`
 * state record, when the stored task has one, is left exactly as it is either way: an edit
 * neither clears nor changes it (QZ-D110).
 */
export const applyTaskUpdate = (
  task: Task,
  request: UpdateTaskRequest,
  updatedAt: string
): unknown => {
  const { numericAnswer: _number, sourceReference: _source, ...kept } = task;
  const original = task.translations[task.originalLanguage];
  const keepOthers =
    sameTranslation(original, request.original) && sameTaskTypes(task.taskTypes, request.taskTypes);
  const keptTranslations: Record<string, TaskTranslation> = {};
  if (keepOthers) {
    for (const language of Object.keys(task.translations) as Language[]) {
      if (language === task.originalLanguage) {
        continue;
      }
      const translation = task.translations[language];
      if (translation !== undefined && request.targetLanguages.includes(language)) {
        keptTranslations[language] = translation;
      }
    }
  }
  return {
    ...kept,
    taskTypes: request.taskTypes,
    difficulty: request.difficulty,
    tags: request.tags,
    targetLanguages: request.targetLanguages,
    ageGroups: request.ageGroups,
    ...(request.sourceReference === undefined ? {} : { sourceReference: request.sourceReference }),
    explanation: request.explanation,
    ...(request.numericAnswer === undefined ? {} : { numericAnswer: request.numericAnswer }),
    translations: { ...keptTranslations, [task.originalLanguage]: request.original },
    updatedAt,
  };
};
